import { Box, BoxProps } from '@chakra-ui/react'
import React from 'react'
import { AnsweredBy, InternetSearchDecision, MessageThread } from '../types/chatTypes'
import { Answer } from './Answer/Answer'
import { AppliedCommandTags } from './AppliedCommandTags'
import FlagBox from './Answer/FlagBox'
import { InputValidationError } from './InputValidationError'
import { UserChatMessage } from './UserChatMessage'
import { InternetSearchApproval } from './InternetSearchApproval'

interface ChatBubbleProps extends BoxProps {
  thread: MessageThread
  onRetry: () => void
  onToggleResponseMinimized?: (responseMessageId: string) => void
  onResolveInternetSearch?: (decision: InternetSearchDecision, queries?: (string | null)[]) => void
  currentPhase?: {tempId: string, phase: 'internal_streaming' | 'llm_resumed'} | null
  // The specialist that answered this thread; none for the conversation's own agent.
  author?: AnsweredBy
  headerAuthor?: AnsweredBy
  onAskAgain?: (pick: string) => void
  isAskAgainDisabled?: boolean
}

const ChatBubble: React.FC<ChatBubbleProps> = ({
  thread,
  onRetry,
  onToggleResponseMinimized,
  onResolveInternetSearch,
  currentPhase,
  author,
  headerAuthor,
  onAskAgain,
  isAskAgainDisabled,
  ...props
}) => {

  return (
    <Box {...props}>
      {thread.question && (
        <UserChatMessage
          message={thread.question}
          attachedDocuments={thread.attachedAssets}
        />
      )}

      <AppliedCommandTags commands={thread.appliedCommands} />

      {thread.internetSearchApproval?.status === 'pending' && <FlagBox flags={thread.internetSearchApproval.flags} />}

      {thread.internetSearchApproval && (
        <InternetSearchApproval
          key={thread.internetSearchApproval.interrupt_id}
          approval={thread.internetSearchApproval}
          onResolve={onResolveInternetSearch}
        />
      )}

      {/* Show input validation error if present */}
      {thread.inputValidationError && (
        <Box mt={4}>
          <InputValidationError
            error={thread.inputValidationError}
            errorDetails={thread.inputValidationErrorDetails}
            onRetry={onRetry}
          />
        </Box>
      )}

      {thread.answers.sort((a, b) => a.knowledgeSource === 'LLM_ONLY' ? -1 : 1).map((answer, index) => (
        <Box key={answer.message_id} mt={index > 0 ? 4 : 0} data-dd-privacy='mask'>
          <Answer
            answer={answer}
            withFeedback={thread.question !== undefined}
            isLoading={answer.isStreaming && answer.answer === ''}
            onRetry={onRetry}
            onToggleMinimized={onToggleResponseMinimized ? () => onToggleResponseMinimized(answer.message_id) : undefined}
            hasMultipleAnswers={thread.answers.length > 1}
            scopeCommands={thread.appliedCommands}
            author={author}
            headerAuthor={headerAuthor}
            onAskAgain={onAskAgain}
            isAskAgainDisabled={isAskAgainDisabled}
          />
        </Box>
      ))}
    </Box>
  )
}

export default React.memo(ChatBubble)
